import { getLocalizedValue } from "@repo/shared";
/**
 * Client preferences widget (QP-224) — pure, shared by the advisor wizard and
 * the public flow renderer.
 *
 * Lists every client-preference definition of the flow's portfolio collection
 * with an explicit yes/no choice and NO default — each answer is a conscious
 * decision. Every change fires onChange with the full answers map (the parent
 * persists per answer, so the client can leave and resume).
 */

import type { ClientPreferencesComponentInput } from "@repo/shared/types";
import { useEffect, useRef, useState } from "react";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import type { ComponentStyleType } from "@/lib/types/flow";
import { T } from "./client-preferences-widget.i18n";
import { InfoNote } from "./info-note";

/** Slim definition shape carried by the flow-definition payload. */
export type ClientPreferenceItem = {
  id: string;
  nameI18n: Record<string, string>;
};

export type ClientPreferencesWidgetProps = {
  /** Current definitions of the flow collection (empty = feature unused → muted hint). */
  preferences: ClientPreferenceItem[];
  inputData?: string | null;
  onChange: (input: ClientPreferencesComponentInput) => void;
  disabled?: boolean;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  language?: string;
  hasValidationError?: boolean;
  /** QL-188 — group answering note shown under the header; null for non-group clients. */
  groupNote?: string | null;
};

function parseAnswers(inputData: string | null | undefined): Record<string, boolean> {
  if (!inputData) return {};
  try {
    const parsed = JSON.parse(inputData) as ClientPreferencesComponentInput;
    const answers: Record<string, boolean> = {};
    for (const [id, value] of Object.entries(parsed?.answers ?? {})) {
      if (typeof value === "boolean") answers[id] = value;
    }
    return answers;
  } catch {
    return {};
  }
}

/** Order-independent identity of an answers map. */
function answersKey(answers: Record<string, boolean>): string {
  return JSON.stringify(Object.entries(answers).sort(([a], [b]) => a.localeCompare(b)));
}

export function ClientPreferencesWidget({
  preferences,
  inputData,
  onChange,
  disabled = false,
  style = "none",
  title,
  description,
  language = "en_GB",
  hasValidationError = false,
  groupNote = null,
}: ClientPreferencesWidgetProps) {
  const { tenantDefaultLanguage } = useUserLanguage();
  const t = useT(T);

  const [answers, setAnswers] = useState(() => parseAnswers(inputData));

  // Keep the latest onChange without retriggering the save effect on parent re-renders.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // The answers the store holds as far as this widget knows — the last ones it
  // sent or adopted. Seeded with the stored value so mounting never saves it back.
  const syncedKeyRef = useRef(answersKey(answers));

  // Adopt a stored value this widget didn't send (an AI/MCP tool or the public
  // API saved the answers). The save hooks echo our own saves into `inputData`
  // and never write back a superseded save's reply, so an echo matches the
  // synced key and is ignored — no state reset, no remount on a local click.
  useEffect(() => {
    const stored = parseAnswers(inputData);
    const storedKey = answersKey(stored);
    if (storedKey === syncedKeyRef.current) return;
    syncedKeyRef.current = storedKey;
    setAnswers(stored);
  }, [inputData]);

  // Persist when the answers changed locally.
  useEffect(() => {
    if (disabled) return;
    const key = answersKey(answers);
    if (key === syncedKeyRef.current) return;
    syncedKeyRef.current = key;
    onChangeRef.current({ answers });
  }, [answers, disabled]);

  const wrapperClass = hasValidationError
    ? "border border-destructive rounded-lg p-4"
    : style === "border"
      ? "border rounded-lg p-4"
      : style === "box"
        ? "bg-muted/30 rounded-lg p-4"
        : "";

  if (preferences.length === 0) {
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.noPreferences}
      </div>
    );
  }

  return (
    <div className={wrapperClass} data-testid="client-preferences-widget">
      <div className="space-y-4">
        <FlowComponentHeader title={title} description={description} />
        {groupNote && <InfoNote text={groupNote} />}
        {hasValidationError && <p className="text-sm text-destructive">{t.incompleteMessage}</p>}

        {preferences.map((preference) => {
          const answer = answers[preference.id];
          return (
            <div
              key={preference.id}
              className="flex items-center justify-between gap-4 border-b last:border-b-0 pb-3 last:pb-0"
            >
              <Label className="font-normal">
                {getLocalizedValue(preference.nameI18n, language, tenantDefaultLanguage) ||
                  preference.id}
              </Label>
              <RadioGroup
                value={answer === undefined ? "" : answer ? "yes" : "no"}
                onValueChange={(value) =>
                  setAnswers((prev) => ({ ...prev, [preference.id]: value === "yes" }))
                }
                disabled={disabled}
                className="flex gap-4 shrink-0"
              >
                <OptionRow>
                  <RadioGroupItem value="yes" id={`client-preference-${preference.id}-yes`} />
                  <Label
                    htmlFor={`client-preference-${preference.id}-yes`}
                    className="font-normal cursor-pointer"
                  >
                    {t.yes}
                  </Label>
                </OptionRow>
                <OptionRow>
                  <RadioGroupItem value="no" id={`client-preference-${preference.id}-no`} />
                  <Label
                    htmlFor={`client-preference-${preference.id}-no`}
                    className="font-normal cursor-pointer"
                  >
                    {t.no}
                  </Label>
                </OptionRow>
              </RadioGroup>
            </div>
          );
        })}
      </div>
    </div>
  );
}
